import { mainApi } from "services/main/api";
import { GaiaConstants } from "utils/gaia-constants";
import { BackendPropertyFilterQuery } from "components/server-side-table/propertyFilterTransform";

// KB Approval types
export type KBApprovalCategory = "Ingestion" | "Deletion";

export interface KBDocumentInput {
  fileName: string;
  extensionType: string;
  s3Key: string;
  size: string;
  replaceExisting?: boolean;
  tags?: Record<string, string>;
}

export interface SubmitKBApprovalRequest {
  teamName: string;
  documents?: KBDocumentInput[];
  approvers: string[];
  approvalMode: "AND" | "OR";
  messageForApprovers?: string;
  reviewTitle?: string;
  category?: KBApprovalCategory;
  atlasObjectKeys?: string[];
  existingDocumentIds?: string[];
}

export interface SubmitKBApprovalResponse {
  reviewId: string;
  approvalId?: string;
}

export interface KBApprovalDetail {
  reviewId: string;
  reviewParentId: string;
  approvalId?: string;
  reviewTitle?: string;
  teamName: string;
  requestedBy: string;
  status: string;
  messageForReviewers?: string;
  reviewers?: string[];
  createdAt: number;
  dueDate?: number;
  reviewType?: string;
}

export interface KBApprovalDocument {
  kbApprovalDocumentId: string;
  reviewId: string;
  documentId?: string;
  approvalStatus: string;
  rejectionReason?: string;
  reviewedBy?: string[];
  fileName: string;
  size: string;
  extensionType: string;
  replaceExisting?: boolean;
  atlasObjectKey?: string;
  documentSource?: "ATLAS" | "GAIA_OBJECTS";
  parentId?: string;
  gaiaObjectId?: string;
  tags?: Record<string, string>;
}

export interface GetKBApprovalDetailResponse {
  review: KBApprovalDetail;
  batchStatus: string;
  kbApprovalDocuments: KBApprovalDocument[];
  pendingApprovers?: string[];
  currentUserApproverId?: string;
  approvalMode?: "AND" | "OR";
}

export interface ApproveRejectKBDocumentRequest {
  reviewId: string;
  kbApprovalDocumentId: string;
  teamName: string;
  action: "APPROVE" | "REJECT";
  rejectionReason?: string;
}

export interface ApproveRejectKBDocumentResponse {
  status: string;
}

export interface KBApprovalListItem {
  reviewId: string;
  approvalId: string;
  reviewTitle?: string;
  status?: string;
  requestedBy?: string;
  dueDate?: number;
  createdAt?: number;
  approvedDocCount?: number;
  rejectedDocCount?: number;
  pendingDocCount?: number;
  reviewers?: string[];
  modifiedAt?: number;
  reviewType?: string;
}

export interface SearchKBApprovalsRequest {
  teamName: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: string;
  propertyFilterQuery?: BackendPropertyFilterQuery;
}

export interface SearchKBApprovalsResponse {
  reviews: KBApprovalListItem[];
  paginationMetadata: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    pendingDocCounts?: Record<string, number>;
  };
}

export interface ExecuteApproverActionRequest {
  approvalId: string;
  parentId: string;
  approverId: string;
  reviewId: string;
  action: "APPROVE" | "REJECT_APPROVAL";
}

export interface ExecuteApproverActionResponse {
  approval: {
    approvalId: string;
    status: string;
  };
}

const extendedApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    submitKBApproval: builder.mutation<
      SubmitKBApprovalResponse,
      SubmitKBApprovalRequest
    >({
      query: (body) => ({
        url: "kb-approval",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AtlasDocuments", "Approvals"],
    }),
    getKBApprovalDetail: builder.query<
      GetKBApprovalDetailResponse,
      { reviewId: string }
    >({
      query: ({ reviewId }) => ({
        url: `kb-approval/${reviewId}`,
      }),
    }),
    approveRejectKBDocument: builder.mutation<
      ApproveRejectKBDocumentResponse,
      ApproveRejectKBDocumentRequest
    >({
      query: ({ reviewId, kbApprovalDocumentId, ...body }) => ({
        url: `kb-approval/${reviewId}/document/${kbApprovalDocumentId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Approvals"],
    }),
    searchKBApprovals: builder.query<
      SearchKBApprovalsResponse,
      SearchKBApprovalsRequest
    >({
      query: (body) => ({
        url: "kb-approvals",
        method: "POST",
        body,
      }),
      providesTags: ["KBApprovals"],
    }),
    executeKBApproverAction: builder.mutation<
      ExecuteApproverActionResponse,
      ExecuteApproverActionRequest
    >({
      query: (body) => ({
        url: `approval/${body.approvalId}/executeApproverAction`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AtlasDocuments", "Approvals"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(mainApi.util.invalidateTags(["KBApprovals"]));
        }, GaiaConstants.OPENSEARCH_SYNC_DELAY);
      },
    }),
    executeKBRequesterAction: builder.mutation<
      ExecuteApproverActionResponse,
      {
        approvalId: string;
        parentId: string;
        reviewId: string;
        action: string;
        comment?: string;
      }
    >({
      query: (body) => ({
        url: `approval/${body.approvalId}/executeRequesterAction`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AtlasDocuments", "Approvals"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(mainApi.util.invalidateTags(["KBApprovals"]));
        }, GaiaConstants.OPENSEARCH_SYNC_DELAY);
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useSubmitKBApprovalMutation,
  useGetKBApprovalDetailQuery,
  useApproveRejectKBDocumentMutation,
  useSearchKBApprovalsQuery,
  useExecuteKBApproverActionMutation,
  useExecuteKBRequesterActionMutation,
} = extendedApi;
